import { getFocusRingOutlineCSS } from '@ardoq/style-helpers';
import { r4 } from '@ardoq/design-tokens';
import { JSX } from 'react';
import styled from 'styled-components';

export type UnstyledLinkProps = {
  href: string;
  onClick?: VoidFunction;
  onTrack?: VoidFunction;
  target?: '_self' | '_blank';
};

const FocusableLink = styled.a`
  border-radius: ${r4};

  &:focus-visible,
  &.focus {
    ${getFocusRingOutlineCSS()};
  }
`;

/**
 * Unstyled anchor/link component for soft navigation.
 *
 * @param props Props, usually returned by {@link toLinkProps} or {@link toLinkPropsReturningActions}.
 * @returns React node that renders a link.
 */
export const UnstyledLink = ({
  onTrack,
  ...props
}: JSX.IntrinsicElements['a'] & Pick<UnstyledLinkProps, 'onTrack'>) => {
  const target = props.target ?? (props.onClick ? '_self' : '_blank');

  return (
    <FocusableLink
      {...props}
      target={target}
      rel={props.rel ?? (target === '_blank' ? 'noopener' : undefined)}
      onClick={event => {
        onTrack?.();
        if (!isRegularClick(event) || !props.onClick) {
          return;
        }
        event.preventDefault();
        props.onClick?.(event);
      }}
      onAuxClick={event => {
        if (isMiddleClick(event)) {
          onTrack?.();
        }
      }}
    />
  );
};

const isRegularClick = (event: React.MouseEvent): boolean =>
  event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey;

const isMiddleClick = (event: React.MouseEvent): boolean => event.button === 1;
